// src/pages/ReceiptShare.js
import React, { useMemo } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { Box, Container, Card, CardContent, Typography, Stack, Button, Divider, Chip } from '@mui/material'
import {
    ArrowBack as ArrowBackIcon,
    ContentCopy as CopyIcon,
    Home as HomeIcon,
    ReceiptLong as ReceiptIcon,
} from '@mui/icons-material'
import { QRCodeCanvas } from 'qrcode.react'
import { useTransactions } from '../contexts/TransactionsContext'

const money = (amount, currency) => {
    try {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(Number(amount || 0))
    } catch {
        return `${currency} ${Number(amount || 0).toFixed(2)}`
    }
}

const asObject = (value) => {
    if (value && typeof value === 'object' && !Array.isArray(value)) return value
    return {}
}

const firstText = (...values) => {
    for (const value of values) {
        const text = value == null ? '' : String(value).trim()
        if (text) return text
    }
    return ''
}

const copyText = async (value) => {
    try {
        await navigator.clipboard.writeText(value)
    } catch (error) {
        const textarea = document.createElement('textarea')
        textarea.value = value
        textarea.style.position = 'fixed'
        textarea.style.opacity = '0'
        document.body.appendChild(textarea)
        textarea.select()
        document.execCommand('copy')
        document.body.removeChild(textarea)
    }
}

const buildReceiptProof = (tx = {}) => {
    const details = asObject(tx.details || tx.raw?.details)
    const mojoscan = asObject(tx.mojoscan || details.mojoscan)
    const reference = firstText(
        tx.proof_reference,
        tx.proofReference,
        tx.reference,
        tx.paymentReference,
        details.proof_reference,
        details.transaction_reference,
        tx.id
    )
    const receiptNumber = firstText(
        tx.receipt_number,
        tx.receiptNumber,
        tx.accountNumber,
        tx.account,
        mojoscan.receipt_number,
        mojoscan.receiptNumber,
        details.mojoscan_receipt_number,
        reference
    )
    const manualCode = firstText(
        tx.verification_code,
        tx.verificationCode,
        tx.manualCode,
        mojoscan.verification_code,
        mojoscan.verificationCode,
        details.mojoscan_verification_code,
        receiptNumber
    )
    const qrPayload = firstText(
        tx.qrPayload,
        tx.qr_payload,
        tx.mojoscan_qr_payload,
        mojoscan.qr_payload,
        mojoscan.qrPayload,
        details.mojoscan_qr_payload
    )

    const fallbackPayload = JSON.stringify({
        type: 'mojopayr_receipt',
        reference,
        receipt_number: receiptNumber,
        manual_code: manualCode,
        receipt_type: tx.type || details.payment_category || 'payment',
        amount: Number(tx.amount || 0),
        currency: tx.currency || details.currency || 'USD',
    })

    return {
        reference,
        receiptNumber,
        manualCode,
        qrPayload: qrPayload || fallbackPayload,
    }
}

const ReceiptShare = () => {
    const { id } = useParams()
    const navigate = useNavigate()
    const location = useLocation()

    // ✅ use getById so it works reliably with localStorage-backed context
    const { getById } = useTransactions()

    const tx = useMemo(() => {
        const stateTx = location.state?.transaction
        if (stateTx) return stateTx
        if (!id) return null
        return getById(String(id)) || null
    }, [id, getById, location.state])

    const title = tx?.title || 'Receipt'
    const currency = tx?.currency || 'USD'
    const amount = tx?.amount || 0
    const fee = tx?.fee || 0
    const total = tx?.total ?? amount
    const dateText = tx?.createdAt ? new Date(tx.createdAt).toLocaleString() : ''
    const proof = tx ? buildReceiptProof(tx) : null

    return (
        <Box sx={{ py: { xs: 5, md: 7 } }}>
            <Container maxWidth="sm">
                <Card
                    sx={{
                        borderRadius: 6,
                        bgcolor: 'rgba(15,23,42,0.78)',
                        border: '1px solid rgba(255,255,255,0.10)',
                    }}
                >
                    <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                        <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 2 }}>
                            <ReceiptIcon />
                            <Typography variant="h5" sx={{ fontWeight: 950, color: 'white' }}>
                                Receipt
                            </Typography>
                            <Chip label="Shared link" size="small" sx={{ ml: 1 }} />
                        </Stack>

                        {!tx ? (
                            <>
                                <Typography sx={{ color: 'rgba(255,255,255,0.72)', mb: 2 }}>
                                    This receipt is not available on this device.
                                </Typography>

                                <Typography sx={{ color: 'rgba(255,255,255,0.60)', fontSize: 13, mb: 3 }}>
                                    This link works only on the same browser that created the receipt.
                                </Typography>

                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                                    <Button variant="contained" startIcon={<HomeIcon />} onClick={() => navigate('/')}>
                                        Go Home
                                    </Button>
                                    <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>
                                        Go Back
                                    </Button>
                                </Stack>
                            </>
                        ) : (
                            <>
                                <Typography sx={{ color: 'white', fontWeight: 950, fontSize: 18, mb: 1 }}>
                                    {title}
                                </Typography>

                                <Divider sx={{ borderColor: 'rgba(255,255,255,0.10)', my: 2 }} />

                                <Box sx={{ display: 'grid', gap: 1.1 }}>
                                    <Row label="Receipt ID" value={proof?.receiptNumber || tx.id} />
                                    <Row label="Reference" value={proof?.reference || tx.reference || tx.id} />
                                    <Row label="Type" value={tx.type || 'payment'} />
                                    <Row label="Status" value={tx.status || 'completed'} />
                                    <Row label="Amount" value={money(amount, currency)} />
                                    <Row label="Fee" value={money(fee, currency)} />
                                    <Row label="Total" value={money(total, currency)} />
                                    <Row label="Date" value={dateText} />
                                </Box>

                                <Divider sx={{ borderColor: 'rgba(255,255,255,0.10)', my: 2 }} />

                                {proof?.qrPayload && (
                                    <>
                                        <Stack spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                                            <Typography sx={{ color: 'white', fontWeight: 900, alignSelf: 'flex-start' }}>
                                                MojoScan Verification
                                            </Typography>
                                            <Box
                                                sx={{
                                                    bgcolor: '#fff',
                                                    p: 1.5,
                                                    borderRadius: 3,
                                                    display: 'inline-flex',
                                                }}
                                            >
                                                <QRCodeCanvas value={proof.qrPayload} size={190} includeMargin />
                                            </Box>
                                            <Box sx={{ width: '100%', display: 'grid', gap: 1.1 }}>
                                                <CopyRow label="Manual Code" value={proof.manualCode || proof.receiptNumber} />
                                            </Box>
                                        </Stack>

                                        <Divider sx={{ borderColor: 'rgba(255,255,255,0.10)', my: 2 }} />
                                    </>
                                )}

                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                                    <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>
                                        Go Back
                                    </Button>
                                    <Button variant="contained" onClick={() => navigate('/transactions')}>
                                        View in Transactions
                                    </Button>
                                </Stack>
                            </>
                        )}
                    </CardContent>
                </Card>
            </Container>
        </Box>
    )
}

const Row = ({ label, value }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
        <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontWeight: 800 }}>{label}</Typography>
        <Typography sx={{ color: 'white', fontWeight: 900, wordBreak: 'break-word', textAlign: 'right' }}>
            {value}
        </Typography>
    </Box>
)

const CopyRow = ({ label, value }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'center' }}>
        <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontWeight: 800 }}>{label}</Typography>
        <Button
            size="small"
            endIcon={<CopyIcon />}
            onClick={() => copyText(value)}
            sx={{
                color: '#FF8A00',
                fontWeight: 900,
                textTransform: 'none',
                maxWidth: '70%',
                wordBreak: 'break-word',
                textAlign: 'right',
            }}
        >
            {value}
        </Button>
    </Box>
)

export default ReceiptShare
